import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { formatDaysText } from "@intelligems/ig-utils/date-time";
import { useGetOrg } from "@src/api/organizations/get-organization";
import { MODULE_STATIC_CONFIG } from "@src/components/BillingSettings2026/workflow";
import { experienceModulesForModuleId } from "@src/constants/billing";
import { useModuleTrialWelcome } from "@src/features/sharedExperience/hooks/use-module-trial-welcome";
import { useModulesWithExperiences } from "@src/features/sharedExperience/hooks/use-modules-with-experiences";
import {
  type LiveModuleTrial,
  useModuleTrials,
} from "@src/features/sharedExperience/hooks/use-module-trials";
import { useDismissAlert } from "@src/hooks/useDismissAlert";
import { SETTINGS_ROUTES } from "@src/routes/routes";
import { useSelector } from "@src/store";
import { getIsAlertDismissed } from "@src/store/slices/appSettings";
import { SettingsSectionId } from "@src/types/navigation";
import {
  type ModuleTrialEndingResolution,
  mpModuleTrialEndingReminderResolved,
  mpModuleTrialEndingReminderViewed,
} from "@src/utils/mixpanel";
import {
  isTrialDeadlineAhead,
  resolveTrialDeadline,
} from "@src/utils/trial-deadline";

import { ModuleTrialEndingDialogView } from "./ModuleTrialEndingDialog.view";

const REMINDER_THRESHOLD_DAYS = 3;

const endingAlertKey = (trial: LiveModuleTrial) =>
  `module-trial-ending:${trial.moduleId}`;

const endingViewedKey = (trial: LiveModuleTrial) =>
  `module-trial-ending-viewed:${trial.moduleId}`;

const isInReminderWindow = (trial: LiveModuleTrial) => {
  const deadline = resolveTrialDeadline(trial.endDate);
  return (
    isTrialDeadlineAhead(deadline) &&
    (deadline.state === "ends_today" ||
      deadline.remainingDays <= REMINDER_THRESHOLD_DAYS)
  );
};

const daysRemaining = (trial: LiveModuleTrial) => {
  const deadline = resolveTrialDeadline(trial.endDate);
  return deadline.state === "running" ? deadline.remainingDays : 0;
};

const countdownLabel = (days: number) =>
  days > 0 ? `${formatDaysText(days)} left in trial` : "Ends today";

export function ModuleTrialEndingDialog() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const org = useGetOrg();
  const dismissAlert = useDismissAlert();
  const moduleTrials = useModuleTrials();
  const { isPending: isWelcomePending } = useModuleTrialWelcome();

  const orgId = org.data?.org?.id;
  const dismissedModuleIds = useSelector((state) =>
    moduleTrials
      .filter((trial) =>
        getIsAlertDismissed(orgId, endingAlertKey(trial))(state)
      )
      .map((trial) => trial.moduleId)
      .join(",")
  ).split(",");

  const trialsInWindow = moduleTrials
    .filter(
      (trial) =>
        trial.endBehavior === "cancels" &&
        isInReminderWindow(trial) &&
        !dismissedModuleIds.includes(trial.moduleId)
    )
    .map((trial) => ({
      trial,
      modules: experienceModulesForModuleId(trial.moduleId),
    }));
  const modulesToCheck = new Set(
    trialsInWindow.flatMap((ending) => ending.modules)
  );
  const modulesWithExperiences = useModulesWithExperiences(modulesToCheck);
  const endingTrials = trialsInWindow
    .filter((ending) =>
      ending.modules.some((module) =>
        modulesWithExperiences.modules.has(module)
      )
    )
    .map((ending) => ending.trial)
    .sort((a, b) => a.endDate - b.endDate);
  const soonestEnding = modulesWithExperiences.isLoading
    ? undefined
    : endingTrials.at(0);

  const hasRecordedView = useSelector(
    getIsAlertDismissed(
      orgId,
      soonestEnding ? endingViewedKey(soonestEnding) : ""
    )
  );
  const isOpen =
    soonestEnding !== undefined &&
    !pathname.startsWith(SETTINGS_ROUTES.Settings) &&
    !isWelcomePending;

  const firstViewModuleId =
    isOpen && !hasRecordedView ? soonestEnding.moduleId : undefined;
  useEffect(() => {
    if (!firstViewModuleId || !soonestEnding) {
      return;
    }
    mpModuleTrialEndingReminderViewed({
      moduleId: firstViewModuleId,
      daysRemaining: daysRemaining(soonestEnding),
      trialEndDate: new Date(soonestEnding.endDate * 1_000).toISOString(),
    });
    dismissAlert(endingViewedKey(soonestEnding));
  }, [firstViewModuleId]);

  if (!soonestEnding) {
    return null;
  }

  const { moduleId } = soonestEnding;

  const resolve = (resolution: ModuleTrialEndingResolution) => {
    for (const trial of endingTrials) {
      dismissAlert(endingAlertKey(trial));
    }
    mpModuleTrialEndingReminderResolved({ moduleId, resolution });
  };

  const onClose = () => resolve("dismissed");

  const onViewPlans = () => {
    resolve("view_all_plans");
    void navigate(`${SETTINGS_ROUTES.Settings}#${SettingsSectionId.Billing}`);
  };

  const onAddToPlan = () => {
    resolve("add_to_plan");
    void navigate(
      `${SETTINGS_ROUTES.Settings}?selected=${moduleId}#${SettingsSectionId.Billing}`
    );
  };

  return (
    <ModuleTrialEndingDialogView
      isOpen={isOpen}
      countdownLabel={countdownLabel(daysRemaining(soonestEnding))}
      moduleDisplayName={MODULE_STATIC_CONFIG[moduleId]?.name ?? moduleId}
      onClose={onClose}
      onViewPlans={onViewPlans}
      onAddToPlan={onAddToPlan}
    />
  );
}
